import { formatDateBr, hojeMaisDias } from "@/lib/format";
import { CORES } from "@/features/overview/series";
import type { Origem, Periodo, StatusInstituicao, StatusUsuario } from "./api";

export const SITUACAO_ADMIN_LABEL: Record<string, string> = { A: "Ativo", T: "Trial", I: "Inativo", C: "Cancelado", E: "Teste" };
export const SITUACOES_ADMIN = Object.entries(SITUACAO_ADMIN_LABEL).map(([value, label]) => ({ value, label }));
/** Escopo padrão da tela: só quem deveria estar usando. */
export const SITUACOES_PADRAO = ["A", "T"];

type Status = StatusInstituicao | StatusUsuario;

const CINZA = "#94a3b8";

export const STATUS_INFO: Record<Status, { label: string; cor: string }> = {
  OK: { label: "Ativa", cor: CORES.verde },
  ATENCAO: { label: "Atenção", cor: CORES.amarelo },
  INATIVA: { label: "Inativa", cor: CORES.vermelho },
  INATIVO: { label: "Inativo", cor: CORES.vermelho },
  NUNCA: { label: "Nunca acessou", cor: CINZA },
};

/** Mesma cor, rótulo no masculino para usuário ("Ativo"). */
export function rotuloStatus(status: Status, tipo: "instituicao" | "usuario"): string {
  if (status === "OK") return tipo === "usuario" ? "Ativo" : "Ativa";
  return STATUS_INFO[status].label;
}

export const ORIGEM_LABEL: Record<Origem, string> = { W: "Web", M: "App", X: "Web + App" };

const numero = new Intl.NumberFormat("pt-BR");
export const formatNumero = (v?: number | null) => numero.format(v ?? 0);

export function formatPercentual(v?: number | null, casas = 0): string {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(v * 100).toLocaleString("pt-BR", { maximumFractionDigits: casas, minimumFractionDigits: casas })}%`;
}

function hora(valor: string): string {
  return valor.slice(11, 16);
}

/**
 * "hoje às 14:32", "ontem às 09:10" ou "12/10/2026 às 08:00". Direto da string
 * LocalDateTime (sem Date, ver E.2 em lib/format.ts).
 */
export function formatAcesso(valor?: string | null): string {
  if (!valor) return "—";
  const dia = valor.slice(0, 10);
  const prefixo = dia === hojeMaisDias(0) ? "hoje" : dia === hojeMaisDias(-1) ? "ontem" : formatDateBr(dia);
  const h = hora(valor);
  return h ? `${prefixo} às ${h}` : prefixo;
}

/** "há 3 dias", "hoje", "ontem"; ausente = nunca. */
export function formatDiasSemAcesso(dias?: number | null): string {
  if (dias == null) return "—";
  if (dias <= 0) return "hoje";
  if (dias === 1) return "ontem";
  return `há ${formatNumero(dias)} dias`;
}

/** Variação contra o período anterior: "+25%", "−40%", "novo" (antes zero). */
export function tendencia(atual: number, anterior: number): { texto: string; sinal: -1 | 0 | 1 } {
  if (anterior === 0) return atual === 0 ? { texto: "—", sinal: 0 } : { texto: "novo", sinal: 1 };
  const v = (atual - anterior) / anterior;
  if (Math.abs(v) < 0.005) return { texto: "0%", sinal: 0 };
  return { texto: `${v > 0 ? "+" : "−"}${formatPercentual(Math.abs(v))}`, sinal: v > 0 ? 1 : -1 };
}

/** "yyyy-MM-dd" + N dias, aritmética de calendário local (sem toISOString, ver E.2). */
export function somarDias(data: string, dias: number): string {
  const [y, m, d] = data.split("-").map(Number);
  const dt = new Date(y!, m! - 1, d! + dias);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

/** Filtro de data dos usuários como a pessoa pensa: "últimos N dias", "antes de", "depois de", "entre". */
export type ModoData = "qualquer" | "ultimos" | "antes" | "depois" | "entre";

export interface FaixaData {
  modo: ModoData;
  dias?: number;
  de?: string;
  ate?: string;
}

export const FAIXA_VAZIA: FaixaData = { modo: "qualquer" };

/** Converte para o par inclusivo De/Ate que o backend recebe. "Antes de D" exclui D. */
export function faixaParaFiltro(f: FaixaData): { de?: string; ate?: string } {
  switch (f.modo) {
    case "ultimos":
      return f.dias && f.dias > 0 ? { de: hojeMaisDias(-(f.dias - 1)) } : {};
    case "antes":
      return f.ate ? { ate: somarDias(f.ate, -1) } : {};
    case "depois":
      return f.de ? { de: somarDias(f.de, 1) } : {};
    case "entre":
      return { de: f.de || undefined, ate: f.ate || undefined };
    default:
      return {};
  }
}

// Período da página na URL: ?dias=7|30|90|365 ou ?inicio=&fim= (personalizado).
export const PRESETS_DIAS = [7, 30, 90, 365] as const;
export const DIAS_PADRAO = 30;

export function periodoDosDias(dias: number): Periodo {
  return { inicio: hojeMaisDias(-(dias - 1)), fim: hojeMaisDias(0) };
}

const DATA = /^\d{4}-\d{2}-\d{2}$/;

/** Lê o período de ?dias= ou ?inicio=&fim=; inválido cai no padrão de 30 dias. */
export function periodoDaUrl(params: URLSearchParams): { periodo: Periodo; dias?: number } {
  const inicio = params.get("inicio");
  const fim = params.get("fim");
  if (inicio && fim && DATA.test(inicio) && DATA.test(fim) && inicio <= fim) return { periodo: { inicio, fim } };
  const dias = Number(params.get("dias"));
  const d = (PRESETS_DIAS as readonly number[]).includes(dias) ? dias : DIAS_PADRAO;
  return { periodo: periodoDosDias(d), dias: d };
}

export function descreverPeriodo(periodo: Periodo, dias?: number): string {
  if (dias) return `últimos ${dias} dias`;
  return `${formatDateBr(periodo.inicio)} a ${formatDateBr(periodo.fim)}`;
}

export const TOOLTIP_ACESSO =
  "Acesso = login com usuário e senha (portal ou aplicativo). Quem continua logado sem digitar a senha de novo não gera registro. Usuários SILPI/SISTEMA não entram.";
